import { Injectable } from "@angular/core";
import { AttachmentsStore } from "../store/attachments";
import { DeclarationsStore } from "../store/declarations";
import { GeneralDetailsStore } from "../store/general-details";
import { ProductsStoreService } from "../store/products";
import { RequestDetailsStore } from "../store/request-details";
import { ApplicantDetailsStore } from "../store/applicant-details";
import { GeneralStore } from "../store/general";
import { RequestApprovalTypeDetailsStore } from "../store/approval-type-details-store";
import { MCAttachmentUploadStoreService, MCResponse, MCRootStoreService, MC_RESPONSE_STATUS_CODE, MC_UNION_REQUEST_STATUS_CODE, MCUtils, MCFormStateService, MCFormStateStore } from "@maslul/common-services";
import { map, Observable } from "rxjs";
import { ApprovalTypeItem } from "../../models/request/approval-type";
import { FormisFormStoreData } from "../../models/store/formis-form-store-data";
import { ProductItem } from "../../models/request/product-item";
import { ApplicantDetails } from "../../models/applicant/applicant-details";
import { FormisDataContainer } from "../../models/request/formis-data";
import { Guid } from "guid-typescript";
import { Request } from "../../models/request/request";
import { MIUnionRequestHttpService } from "../shared/union-request-http";
import { ProgressService } from "../shared/progress";

@Injectable({
    providedIn: 'root',
})
export class RequestService {


    constructor(
        private requestApprovalTypeDetailsStore: RequestApprovalTypeDetailsStore,
        private generalStore: GeneralStore,
        private mcFormStateService: MCFormStateService,
        private applicantDetailsStore: ApplicantDetailsStore,
        private generalDetailsStore: GeneralDetailsStore,
        private productsStoreService: ProductsStoreService,
        private requestDetailsStore: RequestDetailsStore,
        private attachmentsStore: AttachmentsStore,
        private rootStoreService: MCRootStoreService,
        private declarationsStore: DeclarationsStore,
        private unionRequestHttpService: MIUnionRequestHttpService,
        private attachmentUploadStore: MCAttachmentUploadStoreService,
        private formStateStore: MCFormStateStore,
        private progressService: ProgressService

    ) {

    }

    initMyImportFormState() {
        this.mcFormStateService.setFormState('isMyImport', true);
    }

    clearRequestStore() {
        this.requestApprovalTypeDetailsStore.initStore();
        this.generalStore.initStore();
        this.applicantDetailsStore.initStore();
        this.generalDetailsStore.initStore();
        this.productsStoreService.initStore();
        this.requestDetailsStore.initStore();
        this.attachmentsStore.initStore();
        this.declarationsStore.initStore();
        this.attachmentUploadStore.initStore();
        this.initMyImportFormState();
    }

    getApprovalTypes(): ApprovalTypeItem[] {
        const approvalTypeId = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId;
        const approvalTypeName = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeName;
        const authorityId = this.requestApprovalTypeDetailsStore.getSnapshot().authorityId;

        return [
            {
                approvalTypeId: approvalTypeId,
                approvalTypeName: approvalTypeName,
                authorityId: authorityId
            }
        ]
    }


    setApprovalTypes(approvalTypeItem: ApprovalTypeItem) {
        this.requestApprovalTypeDetailsStore.setApprovalTypeId(approvalTypeItem.approvalTypeId?.toString());
        this.requestApprovalTypeDetailsStore.setAuthorityId(approvalTypeItem.authorityId?.toString());
    }

    getApplicantDetails(): ApplicantDetails | undefined {
        const applicantDetails = this.applicantDetailsStore.getSnapshot().applicant;
        return applicantDetails;
    }

    setApplicantDetails(applicantDetails: ApplicantDetails) {
        this.applicantDetailsStore.setApplicant(applicantDetails);
    }

    getProducts(): ProductItem[] {
        const products: Map<string, FormisFormStoreData> = this.productsStoreService.getSnapshot().products as Map<string, FormisFormStoreData>;

        const productsToSend: ProductItem[] = [];

        for (const [id, product] of products.entries()) {
            const p: ProductItem = {
                id: id,
                formState: this.getFormState(),
                fieldsData: {
                    model: JSON.stringify(this.updateModel(product.formisFormModel)),
                    sectionsSchema: product.schemas
                }
            }
            productsToSend.push(p);
        }
        return productsToSend;
    }

    setProducts(productItems: ProductItem[]) {
        const guids: string[] = [];
        productItems.forEach((productItem: ProductItem) => {
            const product: FormisFormStoreData = new FormisFormStoreData();
            product.formisFormModel = JSON.parse(productItem.fieldsData.model);
            product.schemas = productItem.fieldsData.sectionsSchema;
            const guid = Guid.create().toString();
            guids.push(guid);
            this.productsStoreService.addProduct(product, guid);
        });
        if (guids.length > 0) {
            this.progressService.initProductTabs(guids);
        }
    }

    updateModel(model: any): any {
        if (MCUtils.isObject(model.productDetails.ModelCode)) { return model; }
        model.productDetails.ModelCode = { value: model.productDetails.ModelCode, label: model.productDetails.ModelCode }
        return model;
    }

    getGeneralDetails(): FormisDataContainer {
        const formisFormModel = this.generalDetailsStore.getSnapshot().formisFormModel;
        const stringModel = JSON.stringify(formisFormModel);

        const fieldsSchema = this.generalDetailsStore.getSnapshot().fieldsSchema;
        const generalDetails: FormisDataContainer = {
            model: stringModel,
            sectionsSchema: fieldsSchema
        }
        return generalDetails;

    }

    setGeneralDetails(generalDetails: FormisDataContainer) {
        this.generalDetailsStore.setFieldsSchema(generalDetails.sectionsSchema);
        this.generalDetailsStore.setFormisFormModel(JSON.parse(generalDetails.model))
    }

    getRequestDetails(): FormisDataContainer {
        const formisFormModel = this.requestDetailsStore.getSnapshot().formisFormModel;
        const stringModel = JSON.stringify(formisFormModel);

        const fieldsSchema = this.requestDetailsStore.getSnapshot().fieldsSchema;
        const requestDetails: FormisDataContainer = {
            model: stringModel,
            sectionsSchema: fieldsSchema
        }
        return requestDetails;
    }

    setRequestDetails(requestDetails: FormisDataContainer) {
        this.requestDetailsStore.setFieldsSchema(requestDetails.sectionsSchema);
        this.requestDetailsStore.setFormisFormModel(JSON.parse(requestDetails.model));
    }

    getAttachmentsDetails(): FormisDataContainer {
        const formisFormModel = this.attachmentsStore.getSnapshot().formisFormModel;
        const stringModel = JSON.stringify(formisFormModel);

        const fieldsSchema = this.attachmentsStore.getSnapshot().fieldsSchema;
        const attachments: FormisDataContainer = {
            model: stringModel,
            sectionsSchema: fieldsSchema
        }
        return attachments;
    }

    setAttachmentsDetails(attachments: FormisDataContainer) {
        this.attachmentsStore.setFieldsSchema(attachments.sectionsSchema);
        try {
            this.attachmentsStore.setFormisFormModel(JSON.parse(attachments.model));
        } catch (e) {
            console.error('שגיאת JSON בטעינת מודל קבצים מצורפים:', e);
            this.attachmentsStore.setFormisFormModel({});
        }
    }

    getDeclartionDetails(): FormisDataContainer {
        const formisFormModel = this.declarationsStore.getSnapshot().formisFormModel;
        const stringModel = JSON.stringify(formisFormModel);

        const fieldsSchema = this.declarationsStore.getSnapshot().fieldsSchema;
        const declarations: FormisDataContainer = {
            model: stringModel,
            sectionsSchema: fieldsSchema
        }
        return declarations;
    }

    setDeclartionDetails(declarations: FormisDataContainer) {
        this.declarationsStore.setFieldsSchema(declarations.sectionsSchema);
        try {
            this.declarationsStore.setFormisFormModel(JSON.parse(declarations.model));
        } catch (e) {
            console.error('שגיאת JSON בטעינת מודל הצהרות:', e);
            this.declarationsStore.setFormisFormModel({});
        }
    }

    getFormState() {
        return JSON.stringify(this.mcFormStateService.getFormState());
    }
    setFormState(formStateString: string) {
        try {
            this.formStateStore.setFormState(formStateString);

        } catch (e) {
            console.error('שגיאת JSON בטעינת FormState:', e);
        }
    }
    getLanguage() {
        const language = this.rootStoreService.getSnapshot().language;
        return language;
    }


    setLanguage(language: number) {
        this.rootStoreService.setLanguage(language);
    }

    getRequestStatus(): MC_UNION_REQUEST_STATUS_CODE {
        return this.generalStore.getSnapshot().requestStatus;
    }

    setRequestStatus(requestStatus: MC_UNION_REQUEST_STATUS_CODE) {
        this.generalStore.setRequestStatus(requestStatus);
    }

    loadExistingRequest(unionReqiestId: string): Observable<Request | null> {
        return this.unionRequestHttpService.getExistRequest(unionReqiestId)
            .pipe(
                map((res: MCResponse) => {
                    if (res && res.code === MC_RESPONSE_STATUS_CODE.SUCCESS) {
                        const request: Request = res.data as Request;
                        this.setAllRequestData(request);
                        return request;
                    }
                    else return null;
                }))
        /* return of(requestJson as unknown as Request).pipe(
             tap((request: Request) => {
                 this.setAllRequestData(request);
             })
         );*/
    }

    setAllRequestData(requestData: Request): void {
        this.clearRequestStore();
        this.setLanguage(requestData.language);
        this.setFormState(requestData.formState);
        this.setAttachmentsDetails(requestData.attachmentsDetails);
        this.setRequestDetails(requestData.requestDetails);
        this.setGeneralDetails(requestData.generalDetails);
        this.setDeclartionDetails(requestData.declartionDetails);
        this.setApplicantDetails(requestData.applicantDetails as ApplicantDetails);
        if (requestData.approvalTypes && requestData.approvalTypes.length > 0) {
            this.setApprovalTypes(requestData.approvalTypes[0]);
        }
        this.setProducts(requestData.products);
        this.setRequestStatus(requestData.status)
    }
}